import { useMemo } from 'react';
import type { AxiosInstance } from 'axios';
import { useIsomorphicAxios } from '@unisporkal/federation';

// services
import ContributorService from './contributorService';
import EditorialTilesService from './editorialTiles/editorialTilesService';
import FiltersService from './filters/filtersService';
import LicenseService from './licenseService';
import PreviouslyLicensedService from './previouslyLicensedService';
import AgreementsService from './agreementsService';
import SearchProfileService from './searchProfileService';

export type Api = {
  contributorService: ContributorService;
  editorialTilesService: EditorialTilesService;
  filtersService: FiltersService;
  licenseService: LicenseService;
  previouslyLicensedService: PreviouslyLicensedService;
  agreementsService: AgreementsService;
  searchProfileService: SearchProfileService;
};

/**
 * The service clients for the UniAPI endpoints, built on one http client.
 *
 * A plain function as well as a hook because route middleware runs before
 * React does and needs the same services. `useApi` is this plus the hook's
 * client and its memoisation, which is what keeps the client itself inside
 * this directory: a caller hands one in, and gets services back.
 *
 * It configures nothing on the client it is handed — that belongs to whoever
 * owns the client. `useApi` sets the JSON `Accept` default on the one it
 * takes from federation.
 */
export const createApi = (axios: AxiosInstance): Api => ({
  contributorService: new ContributorService(axios),
  editorialTilesService: new EditorialTilesService(axios),
  filtersService: new FiltersService(axios),
  licenseService: new LicenseService(axios),
  previouslyLicensedService: new PreviouslyLicensedService(axios),
  agreementsService: new AgreementsService(axios),
  searchProfileService: new SearchProfileService(axios),
});

/**
 * Custom React hook that provides service clients for interacting with UniAPI
 * endpoints. Returns initialized service instances (e.g., licenseService) using
 * a shared HTTP client.
 */
const useApi = (): Api => {
  const axios = useIsomorphicAxios();
  axios.defaults.headers.common.Accept = 'application/json';

  return useMemo(() => createApi(axios), [axios]);
};

export default useApi;
